import R from 'ramda'
import { mapSuccessFormActionsToWidgetActions } from './actions'
import { mapFormTriggersToWidgetTriggers } from './triggers'
import {
  isNotNumber,
  lessThan5,
  greaterThan55,
  isNotMultipleOf5
} from './common-functions'

/**
 * Takes the information from a widget configuration form and performs transformations to it to convert from its form-readable
 * format to the format that we need the widget to be in for our backend
 */
const formActionsToTriggers = R.map(
  R.applySpec({
    // Previusly we hard-coded 'twitter' for the platform value,
    // so I have left it in as the default for backwards-compatibility
    // while allowing it to be overridden by other platform values
    platform: R.propOr('twitter', 'platform'),
    description: R.prop('description'),
    failure: R.always([]),
    success: mapSuccessFormActionsToWidgetActions,
    triggers: mapFormTriggersToWidgetTriggers
  })
)

/**
 * Takes a formValues object from the widget form and applies transformations to the actions arrays
 * to create action sets that are compliant with the spec we have on the backend
 * when it encounters an action or trigger it does not recognize, it ignores it and leaves it in the array
 */
const mapFormToTriggersActions = R.evolve({
  actions: formActionsToTriggers,
  draftActions: formActionsToTriggers
})

const addOwnersTweet = formValues =>
  R.evolve({
    actions: R.map(
      R.evolve({
        triggers: R.map(
          R.when(
            R.propEq('type', "Owner's Tweet"),
            R.applySpec({
              type: R.always('FROM'),
              data: R.always([formValues.ownerId]),
              primary: R.T,
              operator: R.always('AND')
            })
          )
        )
      })
    )
  })(formValues)

const parseThreshold = formValues => {
  return R.pipe(
    R.propOr('0', 'threshold'),
    parseInt,
    value => value / 100
  )(formValues)
}

const getModeration = R.applySpec({
  enabled: R.propOr(false, 'moderated'),
  moderators: R.pathOr([], ['moderators', 'handles']),
  automated: R.propOr(false, 'automated'),
  threshold: parseThreshold
})

const getFilterLists = R.pipe(
  R.prop('filterLists'),
  R.cond([
    [Array.isArray, R.identity],
    [R.T, R.always([])]
  ])
)

const getDuplicationFromForm = R.pipe(
  R.prop('duplication'),
  R.cond([
    [isNotNumber, R.always(5)],
    [lessThan5, R.always(5)],
    [greaterThan55, R.always(55)],
    [isNotMultipleOf5, R.always(5)],
    [R.T, R.identity]
  ])
)

const parseFormMosaicConfig = mosaic => {
  if (!mosaic) return

  const { campaignId, duplicatesAllowed, mediaId } = mosaic

  const duplication = duplicatesAllowed ? getDuplicationFromForm(mosaic) : 0

  return {
    campaignId,
    duplication,
    mediaId: mediaId[0]
  }
}

const getMosaic = R.pipe(R.prop('mosaic'), parseFormMosaicConfig)

const parseFormScoreboardConfig = scoreboardConfig => {
  if (!scoreboardConfig) return

  const {
    adsAccountId,
    cardId,
    framePublicId,
    slot,
    pollIntervalMs,
    textFont,
    textColor,
    textSize,
    textGravity,
    textX,
    textY
  } = scoreboardConfig

  // Nothing configured yet: omit the block so widgets without a scoreboard
  // don't carry an empty one.
  if (!adsAccountId && !cardId && !framePublicId) return

  // Omit blank style fields so the service falls back to its defaults, but keep
  // a 0 offset (a meaningful position).
  const numberOrUndefined = value => {
    if (value === '' || value == null) return undefined
    const n = Number(value)
    return Number.isFinite(n) ? n : undefined
  }

  // The goal target lives on the SPEED_THREAD_STOP action (single source), not
  // here, so it is intentionally not serialized onto scoreboardConfig.
  return {
    adsAccountId,
    cardId,
    framePublicId,
    slot: Number(slot) || 0,
    pollIntervalMs: Number(pollIntervalMs) || 0,
    textFont: textFont || undefined,
    textColor: textColor || undefined,
    textSize: numberOrUndefined(textSize),
    textGravity: textGravity || undefined,
    textX: numberOrUndefined(textX),
    textY: numberOrUndefined(textY)
  }
}

const getScoreboardConfig = R.pipe(
  R.prop('scoreboardConfig'),
  parseFormScoreboardConfig
)

// The Pulse Intelligence (FNB) config is a self-contained nested block the Pulse
// cards read and write as a unit; pass it through verbatim so each card owns its
// own shape, and omit it when nothing is configured so non-Pulse widgets don't
// carry an empty block.
const getIntentBased = R.pipe(
  R.prop('intentBased'),
  intentBased =>
    !intentBased || R.isEmpty(intentBased) ? undefined : intentBased
)

const getActions = R.pipe(
  addOwnersTweet,
  mapFormToTriggersActions,
  R.prop('actions')
)

const getDraftActions = R.pipe(mapFormToTriggersActions, R.prop('draftActions'))

const formToWidget = R.applySpec({
  ownerId: R.prop('ownerId'),
  tenantId: R.prop('tenantId'),
  updatedAt: R.prop('updatedAt'),
  widgetData: {
    type: R.prop('type'),
    status: R.prop('status'),
    name: R.prop('name'),
    description: R.prop('description'),
    actions: getActions,
    draftActions: getDraftActions,
    startDate: R.prop('startDate'),
    endDate: R.prop('endDate'),
    moderation: getModeration,
    threadVentureExport: R.pipe(
      R.propOr('[]', 'threadVentureExport'),
      JSON.parse
    ),
    processedTweets: R.propOr([], 'processedTweets'),
    mosaic: getMosaic,
    scoreboardConfig: getScoreboardConfig,
    intentBased: getIntentBased,
    hideInteractionWhenFlagged: R.propOr(false, 'hideInteractionWhenFlagged'),
    enableFiltering: R.propOr(true, 'enableFiltering'),
    enableBlastFiltering: R.propOr(true, 'enableBlastFiltering'),
    filterLists: getFilterLists,
    timezone: R.prop('timezone'),
    codeList: R.prop('codeList'),
    notificationFrequency: R.prop('notificationFrequency')
  }
})

export {
  formToWidget,
  parseFormMosaicConfig,
  parseFormScoreboardConfig,
  parseThreshold,
  mapFormToTriggersActions
}



// WEBPACK FOOTER //
// ./src/pages/widgets/mapping/form-to-widget.js